/**
 * @typedef {import('../../contracts/forms.js').FieldDef} FieldDef
 * @typedef {import('../../contracts/forms.js').FieldDefBase} FieldDefBase
 * @typedef {import('../../contracts/module.js').MountedModule} MountedModule
 * @typedef {import('../../contracts/editor.js').EditorSection} EditorSection
 */

import { createFromTemplate, slot } from '../../core/dom/template.js';
import { createFormField } from '../../components/form-field/form-field.js';
import { ValidationError, ConflictError } from '../../core/http/errors.js';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog.js';
import { createFieldSectionLayout } from '../../components/field-sections/field-section-layout.js';
import { createSpinner } from '../../components/spinner/spinner.js';
import { groupFieldsBySection } from './group-fields-by-section.js';
import { trackVisualViewportHeight } from '../../core/ui/visual-viewport-height.js';
import template from './crud-editor.tpl.js';

/**
 * @typedef {Object} CrudEditorDeps
 * @property {FieldDef[]} fields
 * @property {{ get: (id: string | number, signal?: AbortSignal) => Promise<unknown>, create: (data: Record<string, unknown>, signal?: AbortSignal) => Promise<unknown>, update: (id: string | number, data: Record<string, unknown>, signal?: AbortSignal) => Promise<unknown> }} adapter
 * @property {{ debug: (msg: string, ctx?: object) => void, error: (msg: string, ctx?: object) => void }} logger
 * @property {{ emit: (name: string, data?: unknown) => void }} [eventBus]
 * @property {() => void} [onSaved]
 * @property {(err: Error) => void} [onError]
 * @property {import('../../contracts/forms.js').FieldRegistry} [fieldRegistry]
 * @property {(values: Record<string, unknown>, mode: 'create' | 'edit') => Record<string, string[]> | null} [onBeforeSubmit]
 * @property {EditorSection[]} [sections]
 * @property {import('../../contracts/upload.js').UploadClient} [uploadClient]
 * @property {import('../../contracts/media.js').MediaClient} [mediaClient]
 * @property {import('../../contracts/forms.js').FilePicker} [filePicker] - Forwarded to the file-field driver ctx (library picker capability: browse + modal open).
 * @property {import('../../contracts/forms.js').FormPicker} [formPicker] - Forwarded to the form-link driver ctx (create/copy-from form-builder modal opener).
 * @property {import('../../contracts/locale.js').LocaleConfig} [localeConfig] - Forwarded to form-field/driver ctx for locale-aware drivers (date/datetime/time).
 * @property {import('../../contracts/field-sections.js').FieldSectionDef[]} [fieldSections]
 * @property {'tabs' | 'accordion'} [fieldLayout]
 */

/**
 * Whether a pre-codec UI value counts as "empty" for a required field.
 * Empty (blocks submit): undefined, null, '', whitespace-only string, empty array.
 * NOT empty (valid): 0, '0', false, non-empty string/array, any object.
 * Exported for direct unit testing — the text driver stringifies its value, so
 * the raw-value matrix can only be proven against this helper directly.
 *
 * @param {unknown} v
 * @returns {boolean}
 */
export function isEmptyRequiredValue(v) {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false; // 0, false, non-empty string, objects → present
}

/**
 * Creates a CrudEditor module — a create/edit overlay form driven by field defs.
 *
 * @param {HTMLElement} el
 * @param {CrudEditorDeps} deps
 * @returns {MountedModule & { open: (id?: string | number, options?: { prefill?: Record<string, unknown> }) => void, isDirty: () => boolean }}
 */
export function createCrudEditor(el, deps) {
  const { fields, adapter, logger } = deps;

  // --- Controller split ---
  // editorSessionController: aborted only on close() — sections use this signal and survive saves.
  // requestController: aborted per load/save operation.
  /** @type {AbortController | null} */
  let editorSessionController = null;
  /** @type {AbortController | null} */
  let requestController = null;

  /** @type {string | number | undefined} */
  let editId;
  /** @type {number} */
  let openVersion = 0;
  /** @type {HTMLElement | null} */
  let previousFocus = null;
  /** @type {string} */
  let savedOverflow = '';
  /** Active while the editor is open on a phone — pins the overlay to the visible
   * viewport so the footer/last field stay reachable above the on-screen keyboard. */
  let vvHandle = /** @type {{ destroy: () => void } | null} */ (null);
  /** Spinner shown over the body while an edit-mode record loads. Owned like
   * vvHandle: mounted in open(), torn down in doTeardown() (covers the stale
   * early-return paths) + on load success/error. */
  let loadSpinner = /** @type {{ destroy: () => void } | null} */ (null);
  /** True while adapter.get() is in flight for an edit-mode open. Gates Save
   * (disabled button + handleSubmit guard) so a mid-load save can't abort the
   * fetch and PUT empty/default fields over the record. */
  let isLoadingRecord = false;

  /** @type {import('../../components/form-field/form-field.js').FormFieldInstance[]} */
  let fieldInstances = [];
  /** @type {Array<{ destroy: () => void }>} */
  let sectionHandles = [];
  /** @type {import('../../contracts/field-sections.js').FieldSectionLayoutHandle | null} */
  let sectionLayoutHandle = null;

  /** @type {'create' | 'edit'} */
  let editorMode = 'create';
  /** @type {Record<string, unknown>} */
  let currentValues = {};
  /** @type {Record<string, boolean>} */
  let visibilityState = {};
  /** @type {boolean} */
  let submitting = false;
  /** @type {boolean} */
  let isDirty = false;
  /** @type {boolean} */
  let isClosed = true; // overlay starts hidden
  /** @type {boolean} */
  let promptInFlight = false;

  /**
   * Returns a shallow snapshot of current form values.
   * Always use this instead of passing currentValues directly — drivers that call
   * ctx.getFormValues() get an isolated snapshot, not the live object.
   * @returns {Record<string, unknown>}
   */
  function getValuesSnapshot() {
    return { ...currentValues };
  }

  // Build overlay DOM from template
  const overlay = createFromTemplate(template);
  const title = slot(overlay, 'title');
  const conflictDiv = /** @type {HTMLElement} */ (slot(overlay, 'conflict'));
  const formBody = slot(overlay, 'body');
  const cancelBtn = slot(overlay, 'cancel');
  const saveBtn = slot(overlay, 'save');
  const closeBtn = /** @type {HTMLElement} */ (overlay.querySelector('.crud-editor-close'));
  const panel = /** @type {HTMLElement} */ (overlay.querySelector('.crud-editor-panel'));

  document.body.appendChild(overlay);

  // Event listeners
  closeBtn.addEventListener('click', () => close());
  cancelBtn.addEventListener('click', () => close());
  saveBtn.addEventListener('click', handleSubmit);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  /** @param {KeyboardEvent} e */
  function handleKeydown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    // Focus trap
    if (e.key === 'Tab') {
      const focusable = /** @type {HTMLElement[]} */ (
        Array.from(panel.querySelectorAll(
          'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
        ))
      ).filter(focusEl => !focusEl.hasAttribute('disabled'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  /**
   * Reactive handler: called when any field value changes.
   * Runs a fixed-point loop to handle cascading visibility/dependency changes.
   * @param {string} key
   * @param {unknown} value
   */
  function handleFieldChange(key, value) {
    isDirty = true;
    currentValues[key] = value;

    let changedKeys = new Set([key]);
    let iterations = 0;

    // Fixed-point loop; guarded by the `iterations` counter below.
    while (true) {
      let dirty = false;
      // nextChangedKeys accumulates newly-changed keys this iteration only.
      // Original trigger key only drives first pass.
      const nextChangedKeys = /** @type {Set<string>} */ (new Set());

      // --- Visibility pass: only fires when visibility actually transitions ---
      for (let i = 0; i < fields.length; i++) {
        const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
        if (typeof def.visible !== 'function') continue;

        const nextVisible = def.visible(currentValues);
        if (!Object.is(nextVisible, visibilityState[def.key])) {
          visibilityState[def.key] = nextVisible;
          fieldInstances[i].setVisible(nextVisible);

          if (!nextVisible) {
            // Field just hidden: disable it
            fieldInstances[i].setDisabled(true);
            // Invalidate unless 'keep'
            if (def.invalidate !== 'keep') {
              fieldInstances[i].clearValue();
              const prev = currentValues[def.key];
              currentValues[def.key] = fieldInstances[i].getValue();
              if (!Object.is(currentValues[def.key], prev)) {
                nextChangedKeys.add(def.key);
                dirty = true;
              }
            }
          } else {
            // Field just shown: restore effective disabled state
            const effectiveDisabled = !!def.disabled || def.disabledOn === editorMode;
            fieldInstances[i].setDisabled(effectiveDisabled);
          }
        }
      }

      // --- DependsOn pass: invalidate/reload fields whose dependencies changed ---
      for (let i = 0; i < fields.length; i++) {
        const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
        if (!def.dependsOn?.some(k => changedKeys.has(k))) continue;

        // Invariant: invalidate runs regardless of visibility (keeps currentValues consistent).
        // Reload runs only when visible (avoids wasted async work).
        // Both use post-clear values via getValuesSnapshot().
        if (def.invalidate !== 'keep') {
          fieldInstances[i].clearValue();
          const prev = currentValues[def.key];
          // Read back BEFORE reload so snapshot reflects cleared state
          currentValues[def.key] = fieldInstances[i].getValue();
          if (!Object.is(currentValues[def.key], prev)) {
            nextChangedKeys.add(def.key);
            dirty = true;
          }
        }

        // Reload only when visible
        if (visibilityState[def.key] !== false) {
          fieldInstances[i].getOptionsProvider?.()?.load('', getValuesSnapshot());
        }
      }

      // Replace with only newly-changed keys; original trigger key only drives first iteration.
      changedKeys = nextChangedKeys;

      if (!dirty) break;

      iterations++;
      if (iterations >= 10) {
        logger.debug('[crud-editor] reactive loop exceeded 10 iterations — possible cycle', { data: { keys: [...changedKeys] } });
        break;
      }
    }
  }

  /**
   * Enter the edit-mode loading state: cover the body with a spinner, make the
   * (already-built but value-less) body non-focusable so Tab can't reach ghost
   * fields, and disable Save. Idempotent guards via isLoadingRecord.
   */
  function beginRecordLoad() {
    isLoadingRecord = true;
    overlay.classList.add('crud-editor--loading');
    overlay.setAttribute('aria-busy', 'true');
    // inert removes the body subtree from BOTH the tab order and the a11y tree
    // (the focus trap queries the panel live, and body inputs have no explicit
    // tabindex, so inert is what actually excludes them).
    formBody.setAttribute('inert', '');
    formBody.setAttribute('aria-hidden', 'true');
    saveBtn.setAttribute('disabled', 'disabled');
    if (!loadSpinner) loadSpinner = createSpinner(formBody, { label: 'Loading…' });
  }

  /**
   * Leave the loading state: destroy the spinner and restore focusability +
   * aria-busy + Save. Called on EVERY load exit (success / error / abort) and
   * from doTeardown() (covers the stale early-return + destroy/reopen paths), so
   * the promise callbacks never need their own spinner cleanup.
   */
  function endRecordLoad() {
    isLoadingRecord = false;
    if (loadSpinner) { loadSpinner.destroy(); loadSpinner = null; }
    overlay.classList.remove('crud-editor--loading');
    overlay.setAttribute('aria-busy', 'false');
    formBody.removeAttribute('inert');
    formBody.removeAttribute('aria-hidden');
    saveBtn.removeAttribute('disabled');
  }

  /**
   * @param {string | number} [id]
   * @param {{ prefill?: Record<string, unknown> }} [options]
   */
  function open(id, options) {
    // open() is public and may be called while ALREADY open (e.g. duplicate →
    // prefill, or a programmatic re-open with no close in between). Fully tear
    // down the live session first so per-open state captured once-per-transition
    // — savedOverflow, previousFocus, the visual-viewport tracker — is restored
    // before we re-capture it. Without this, the second open() would snapshot
    // the already-locked `overflow: hidden` as `savedOverflow` and the eventual
    // close would leave the page permanently scroll-locked (and previousFocus /
    // vvHandle would leak). doTeardown() is a no-op when already closed.
    doTeardown();

    isClosed = false;
    openVersion++;
    const thisVersion = openVersion;

    editorSessionController = new AbortController();
    requestController = new AbortController();

    editId = id;
    editorMode = id !== undefined ? 'edit' : 'create';

    // Save focus and lock scroll (now from a guaranteed-clean closed state).
    previousFocus = /** @type {HTMLElement | null} */ (document.activeElement);
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // On a phone the full-screen overlay is fixed to the layout viewport, which
    // the on-screen keyboard does not shrink — so the footer/last field hide
    // behind the keyboard and the body can't scroll to the end. Pin the overlay
    // to the visible slice while open. No-op on desktop / engines without the API.
    if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches) {
      vvHandle = trackVisualViewportHeight(overlay);
    }

    // Reset reactive state
    currentValues = {};
    visibilityState = {};
    submitting = false;
    isDirty = false;

    // Clear form
    destroyFields();
    title.textContent = id !== undefined ? 'Edit' : 'Add';
    overlay.style.display = '';
    overlay.classList.remove('crud-editor--loading');
    overlay.setAttribute('aria-busy', 'false');
    saveBtn.removeAttribute('disabled');

    // Build fields and sections
    buildFields();

    document.addEventListener('keydown', handleKeydown);

    if (id !== undefined) {
      // Edit mode — show the spinner + lock the body, and park focus on Cancel
      // (a control outside the inert body) so Tab can't walk the background page
      // while the record loads; focusFirstField() runs once values arrive.
      beginRecordLoad();
      cancelBtn.focus();
      adapter.get(id, requestController.signal)
        .then((data) => {
          if (openVersion !== thisVersion) return; // Stale — doTeardown already cleaned up
          endRecordLoad();
          populateFields(/** @type {Record<string, unknown>} */ (data));
          // Sync currentValues after population
          syncCurrentValues();
          // Evaluate visibility with loaded values (no clearing — loaded values are authoritative)
          initVisibility();
          // Reload dependent option fields with initial context values
          initDependsOnReload();
          focusFirstField();
        })
        .catch((err) => {
          if (openVersion !== thisVersion) return; // Stale — doTeardown already cleaned up
          if (err instanceof Error && err.name === 'AbortError') {
            endRecordLoad();
            return;
          }
          endRecordLoad();
          logger.error('Failed to load record', { data: err });
          // Surface the failure to the user instead of opening an empty form.
          // eventBus is optional — optional-chain so a missing bus can't throw
          // before close() and strand the (never-revealed) editor open.
          deps.eventBus?.emit('toast.message.requested', {
            message: 'Could not load this record. Please try again.',
            type: 'error',
          });
          if (deps.onError) deps.onError(/** @type {Error} */ (err));
          close();
        });
    } else {
      // Create mode — apply defaultValues then optional prefill
      applyDefaultValues();
      if (options?.prefill) {
        populateFields(options.prefill);
      }
      // Sync currentValues after defaults/prefill
      syncCurrentValues();
      // Evaluate initial visibility (no clearing on init — fields start in driver-mounted state)
      initVisibility();
      // Reload dependent option fields with initial context values
      initDependsOnReload();
      focusFirstField();
    }
  }

  function buildFields() {
    // Mount all fields
    for (let i = 0; i < fields.length; i++) {
      const def = fields[i];
      const instance = createFormField(formBody, {
        def,
        onChange: (value) => handleFieldChange(def.key, value),
        registry: deps.fieldRegistry,
        getFormValues: getValuesSnapshot,
        uploadClient: deps.uploadClient,
        mediaClient: deps.mediaClient,
        filePicker: deps.filePicker,
        formPicker: deps.formPicker,
        localeConfig: deps.localeConfig,
      });
      fieldInstances.push(instance);
    }

    // Set data-field-key on each field wrapper (used by after-field section positioning)
    for (let i = 0; i < fields.length; i++) {
      const wrapper = fieldInstances[i].getWrapper();
      wrapper.setAttribute('data-field-key', fields[i].key);
    }

    // Populate initial currentValues snapshot
    for (let i = 0; i < fields.length; i++) {
      currentValues[fields[i].key] = fieldInstances[i].getValue();
    }

    // Mount field section layout if configured
    if (deps.fieldSections?.length) {
      const { sectionFields, unsectionedFields } = groupFieldsBySection(
        fields, fieldInstances, deps.fieldSections
      );
      sectionLayoutHandle = createFieldSectionLayout(formBody, {
        sections: deps.fieldSections,
        sectionFields,
        unsectionedFields,
      }, { engine: deps.fieldLayout ?? 'tabs' });
    }

    // Mount sections
    if (deps.sections?.length) {
      const positionOrder = /** @type {Record<string, number>} */ ({ 'before-fields': 0, 'after-field': 1, 'after-fields': 2 });
      const sorted = [...deps.sections].sort((a, b) => {
        const pa = positionOrder[a.position ?? 'after-fields'];
        const pb = positionOrder[b.position ?? 'after-fields'];
        if (pa !== pb) return pa - pb;
        return (a.order ?? 0) - (b.order ?? 0);
      });

      // before-fields sections must be inserted in reverse sorted order so that
      // section with order=1 ends up first in the DOM (each insertAdjacentElement
      // 'afterbegin' pushes the previous one down).
      const beforeSections = sorted.filter(s => (s.position ?? 'after-fields') === 'before-fields');
      const otherSections = sorted.filter(s => (s.position ?? 'after-fields') !== 'before-fields');
      const orderedSections = [...[...beforeSections].reverse(), ...otherSections];

      for (const section of orderedSections) {
        const sectionEl = document.createElement('div');
        sectionEl.className = 'editor-section';

        const position = section.position ?? 'after-fields';

        if (position === 'before-fields') {
          formBody.insertAdjacentElement('afterbegin', sectionEl);
        } else if (position === 'after-field' && section.afterFieldKey) {
          const fieldWrapper = formBody.querySelector(`[data-field-key="${section.afterFieldKey}"]`);
          if (fieldWrapper) {
            fieldWrapper.insertAdjacentElement('afterend', sectionEl);
          } else {
            formBody.appendChild(sectionEl);
          }
        } else {
          formBody.appendChild(sectionEl);
        }

        const sectionHandle = section.render(sectionEl, {
          recordId: editId,
          signal: editorSessionController?.signal,
        });
        sectionHandles.push(sectionHandle);
      }
    }
  }

  /**
   * Sync currentValues from current field getValue() results.
   */
  function syncCurrentValues() {
    for (let i = 0; i < fields.length; i++) {
      currentValues[fields[i].key] = fieldInstances[i].getValue();
    }
  }

  /**
   * Initial visibility pass — snapshot only, no clearing.
   * clearValue is NEVER called during initialization.
   */
  function initVisibility() {
    for (let i = 0; i < fields.length; i++) {
      const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
      if (typeof def.visible !== 'function') continue;
      const visible = def.visible(currentValues);
      visibilityState[def.key] = visible;
      fieldInstances[i].setVisible(visible);
      if (!visible) {
        fieldInstances[i].setDisabled(true);
      }
    }
    // Apply disabledOn after visibility
    applyDisabledOn();
  }

  /**
   * Initial dependsOn reload pass — fires after defaults/prefill/edit population so
   * dependent async option fields load with the correct initial form values context.
   * Only fires for visible fields that have dependsOn and expose an options provider.
   */
  function initDependsOnReload() {
    const snapshot = getValuesSnapshot();
    for (let i = 0; i < fields.length; i++) {
      const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
      if (!def.dependsOn?.length) continue;
      if (visibilityState[def.key] === false) continue;
      fieldInstances[i].getOptionsProvider?.()?.load('', snapshot);
    }
  }

  function applyDisabledOn() {
    for (let i = 0; i < fields.length; i++) {
      const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
      if (!def.disabledOn) continue;
      // Skip fields already hidden (they are already disabled)
      if (visibilityState[def.key] === false) continue;
      if (def.disabledOn === editorMode) {
        fieldInstances[i].setDisabled(true);
      }
    }
  }

  function applyDefaultValues() {
    for (let i = 0; i < fields.length; i++) {
      const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
      if (def.defaultValue === undefined) continue;
      const value = typeof def.defaultValue === 'function' ? def.defaultValue() : def.defaultValue;
      fieldInstances[i].setValue(value);
    }
  }

  /**
   * @param {Record<string, unknown>} data
   */
  function populateFields(data) {
    for (let i = 0; i < fields.length; i++) {
      const key = fields[i].key;
      if (key in data) {
        const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
        const raw = data[key];
        const value = def.codec?.parse ? def.codec.parse(raw) : raw;
        fieldInstances[i].setValue(value);
      }
    }
  }

  function focusFirstField() {
    // Narrow selector — only inputs, textareas, selects, and date-picker
    // triggers (which are <button type="button">). Generic <button> elements
    // (e.g. file driver "Choose file") are intentionally excluded so they
    // don't grab initial focus over a real field.
    const candidates = /** @type {HTMLElement[]} */ (Array.from(formBody.querySelectorAll(
      'input:not([type="hidden"]), textarea, select, button.date-picker__trigger'
    )));
    for (const el of candidates) {
      if (!el.closest('[hidden]') && !el.matches(':disabled')) {
        el.focus();
        return;
      }
    }
  }

  function collectValues() {
    /** @type {Record<string, unknown>} */
    const data = {};
    for (let i = 0; i < fields.length; i++) {
      const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
      const uiValue = fieldInstances[i].getValue();
      const serialized = def.codec?.serialize ? def.codec.serialize(uiValue) : uiValue;
      if (serialized === undefined) continue;
      data[fields[i].key] = serialized;
    }
    return data;
  }

  function hideConflict() {
    conflictDiv.hidden = true;
    conflictDiv.textContent = '';
  }

  function showConflict() {
    conflictDiv.textContent = '';
    conflictDiv.hidden = false;

    const msg = document.createElement('span');
    msg.textContent = 'This record was modified by someone else.';
    conflictDiv.appendChild(msg);

    const reloadBtn = document.createElement('button');
    reloadBtn.type = 'button';
    reloadBtn.className = 'btn';
    reloadBtn.textContent = 'Reload and try again';
    reloadBtn.addEventListener('click', () => {
      if (editId === undefined) return;
      const thisVersion = openVersion;

      if (requestController) requestController.abort();
      requestController = new AbortController();

      // Reuse the shared edit-load lifecycle so Save is BLOCKED during the reload
      // (disabled + isLoadingRecord guard) — otherwise a save mid-reload would
      // abort this adapter.get and PUT stale field values over the record, the
      // same data-loss class the initial load guards against.
      beginRecordLoad();
      // Move focus into the modal BEFORE disabling the reload button — a disabled
      // element loses focus, which would otherwise fall to <body> and let the user
      // Tab out to background controls. Cancel is always present and outside the
      // inert body. focusFirstField() takes over once the fresh data is in.
      cancelBtn.focus();
      reloadBtn.disabled = true;

      adapter.get(editId, requestController.signal)
        .then((data) => {
          if (openVersion !== thisVersion) return; // stale — doTeardown cleaned up
          endRecordLoad();
          populateFields(/** @type {Record<string, unknown>} */ (data));
          syncCurrentValues();
          initVisibility();
          initDependsOnReload();
          isDirty = false;
          hideConflict(); // tears down the conflict UI (incl. the reload button)
          focusFirstField(); // fresh data is in — move focus from Cancel onto the first field
        })
        .catch((err) => {
          if (openVersion !== thisVersion) return; // stale — doTeardown cleaned up
          endRecordLoad();
          if (err instanceof Error && err.name === 'AbortError') return;
          reloadBtn.disabled = false;
          reloadBtn.focus(); // conflict UI stays; return focus so retry is keyboard-reachable
          logger.error('Conflict reload failed', { data: err });
          // Surface the reload failure (the conflict UI stays so the user can retry).
          deps.eventBus?.emit('toast.message.requested', {
            message: 'Could not reload this record. Please try again.',
            type: 'error',
          });
        });
    });
    conflictDiv.appendChild(reloadBtn);
  }

  function clearAllErrors() {
    hideConflict();
    for (const instance of fieldInstances) {
      instance.setErrors([]);
    }
  }

  /**
   * Moves focus to the first field that has an error.
   * @param {string[]} errorKeys
   */
  function focusFirstErrorField(errorKeys) {
    for (let i = 0; i < fields.length; i++) {
      if (!errorKeys.includes(fields[i].key)) continue;
      const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
      // Activate section containing error field so it becomes visible
      if (sectionLayoutHandle && def.section) {
        sectionLayoutHandle.activateSection(def.section);
      }
      const wrapper = fieldInstances[i].getWrapper();
      const focusable = /** @type {HTMLElement | null} */ (wrapper.querySelector(
        'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled)'
      ));
      if (focusable) { focusable.focus(); return; }
    }
  }

  /**
   * Leave the submit/busy state: drop the loading overlay, re-enable Save, and
   * release the `submitting` latch. Single owner of this teardown so the three
   * submit exits (required-fail, onBeforeSubmit-fail, post-adapter finally) can't
   * drift apart.
   */
  function clearBusyState() {
    overlay.classList.remove('crud-editor--loading');
    overlay.setAttribute('aria-busy', 'false');
    saveBtn.removeAttribute('disabled');
    submitting = false;
  }

  async function handleSubmit() {
    // Block save while the record is still loading. The Save button is also
    // disabled during load, but Enter-to-submit / programmatic paths bypass a
    // disabled button — without this guard, submitting mid-load would abort the
    // in-flight adapter.get (shared requestController) and PUT empty/default
    // fields over the record (full-replace data loss).
    if (isLoadingRecord) return;
    if (submitting) return;
    submitting = true;

    const thisVersion = openVersion;

    if (requestController) {
      requestController.abort();
    }
    requestController = new AbortController();

    clearAllErrors();
    overlay.classList.add('crud-editor--loading');
    overlay.setAttribute('aria-busy', 'true');
    saveBtn.setAttribute('disabled', 'disabled');

    // Collect pre-codec UI values for onBeforeSubmit (receives raw UI values, not wire values)
    const uiValues = /** @type {Record<string, unknown>} */ ({});
    for (let i = 0; i < fields.length; i++) {
      uiValues[fields[i].key] = fieldInstances[i].getValue();
    }

    // Generic required-field validation — runs BEFORE onBeforeSubmit so a page's
    // custom validator sees a form that already cleared the baseline "required"
    // check. Only VISIBLE, config-ENABLED required fields are validated: a hidden
    // field or one disabled by config (`disabled` / `disabledOn === editorMode`)
    // is skipped; a field merely disabled at the DOM level (e.g. async-select
    // during option load) is still validated (we key off config, not live DOM).
    const requiredErrorKeys = [];
    for (let i = 0; i < fields.length; i++) {
      const def = /** @type {FieldDefBase} */ (/** @type {unknown} */ (fields[i]));
      if (!def.required) continue;
      const enabledForValidation =
        visibilityState[def.key] !== false && !def.disabled && def.disabledOn !== editorMode;
      if (!enabledForValidation) continue;
      if (isEmptyRequiredValue(uiValues[def.key])) {
        fieldInstances[i].setErrors(['This field is required.']);
        requiredErrorKeys.push(def.key);
      }
    }
    if (requiredErrorKeys.length > 0) {
      focusFirstErrorField(requiredErrorKeys);
      clearBusyState();
      return;
    }

    // onBeforeSubmit hook — client-side validation before adapter call.
    // Runs BEFORE collectValues() (codec serialize) so validation never transforms a value
    // it is about to reject; a failing validator means serialize is not called at all.
    if (deps.onBeforeSubmit) {
      const errors = deps.onBeforeSubmit(uiValues, editorMode);
      if (errors !== null) {
        for (let i = 0; i < fields.length; i++) {
          const key = fields[i].key;
          if (errors[key]) {
            fieldInstances[i].setErrors(errors[key]);
          }
        }
        focusFirstErrorField(Object.keys(errors));
        clearBusyState();
        return;
      }
    }

    const data = collectValues();

    logger.debug('Submitting', { data: { editId, values: data } });

    try {
      if (editId !== undefined) {
        await adapter.update(editId, data, requestController.signal);
      } else {
        await adapter.create(data, requestController.signal);
      }

      if (openVersion !== thisVersion) return; // Stale

      if (deps.eventBus) {
        deps.eventBus.emit('toast.message.requested', {
          message: editId !== undefined ? 'Record updated' : 'Record created',
          type: 'success',
        });
      }

      isDirty = false;
      close({ force: true });
      if (deps.onSaved) deps.onSaved();
    } catch (err) {
      if (openVersion !== thisVersion) return;
      if (err instanceof Error && err.name === 'AbortError') return;

      if (err instanceof ValidationError) {
        for (let i = 0; i < fields.length; i++) {
          const key = fields[i].key;
          if (err.fields[key]) {
            fieldInstances[i].setErrors(err.fields[key]);
          }
        }
        focusFirstErrorField(Object.keys(err.fields));
      } else if (err instanceof ConflictError) {
        showConflict();
      } else {
        logger.error('Submit failed', { data: err });
        // Ordinary save failures (network / server / 5xx) must reach the user —
        // validation shows inline errors and conflict shows its own UI, but this
        // branch was otherwise silent. eventBus is optional → optional-chain.
        deps.eventBus?.emit('toast.message.requested', {
          message: 'Could not save changes. Please try again.',
          type: 'error',
        });
        if (deps.onError) deps.onError(/** @type {Error} */ (err));
      }
    } finally {
      // Only the current submit clears the busy state — a stale submit (superseded
      // by a newer open/close) must leave it to whoever is now current.
      if (openVersion === thisVersion) clearBusyState();
    }
  }

  /**
   * @param {{ force?: boolean }} [opts]
   */
  async function close(opts) {
    if (opts?.force) {
      promptInFlight = false;
      doTeardown();
      return;
    }
    if (isClosed) return;
    if (promptInFlight) return;
    if (isDirty) {
      const versionBefore = openVersion;
      promptInFlight = true;
      let confirmed = false;
      try {
        confirmed = await confirmDialog({
          title: 'Discard changes?',
          message: 'You have unsaved changes. Close anyway?',
          confirmLabel: 'Discard',
          cancelLabel: 'Keep editing',
        });
      } finally {
        promptInFlight = false;
      }
      if (openVersion !== versionBefore) return;
      if (!confirmed) return;
    }
    doTeardown();
  }

  function doTeardown() {
    if (isClosed) return;
    isClosed = true;
    openVersion++; // Invalidate all pending async callbacks that captured thisVersion
    if (vvHandle) { vvHandle.destroy(); vvHandle = null; } // restores the overlay's inline styles
    endRecordLoad(); // destroy any in-flight load spinner + clear inert/disabled (no-op if idle)
    overlay.style.display = 'none';
    overlay.setAttribute('aria-busy', 'false');
    document.body.style.overflow = savedOverflow;
    document.removeEventListener('keydown', handleKeydown);
    // Null abort controllers before destroying fields (fields may read signal)
    if (editorSessionController) { editorSessionController.abort(); editorSessionController = null; }
    if (requestController) { requestController.abort(); requestController = null; }
    destroyFields();
    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    previousFocus = null;
  }

  function destroyFields() {
    // Destroy section layout first — restores field wrappers to formBody
    if (sectionLayoutHandle) {
      sectionLayoutHandle.destroy();
      sectionLayoutHandle = null;
    }

    for (const instance of fieldInstances) {
      instance.destroy();
    }
    fieldInstances = [];

    for (const handle of sectionHandles) {
      handle.destroy();
    }
    sectionHandles = [];
  }

  function destroy() {
    close({ force: true });
    overlay.remove();
  }

  return { destroy, open, isDirty: () => isDirty };
}
